import type { AiComponentCompileError } from '@Pages/pageRuntime/compiler/AiComponentCompileError';
import type {
  CompiledAiComponent,
  PageRuntimeSourceKind
} from '@Pages/pageRuntime/compiler/compileAiComponent';
import type { EditorTransformDiagnostic } from '@Pages/pageRuntime/compiler/editorTransformProtocol';

export type EditorPreviewStatus = 'initializing' | 'ready' | 'stale';

export type PageRuntimeRootKind = Exclude<PageRuntimeSourceKind, 'shared'>;

export type CommittedRootSource = {
  code: string;
  compiled: CompiledAiComponent;
};

export type AuthoringRuntime = {
  status: EditorPreviewStatus;
  diagnostic: EditorTransformDiagnostic | null;
  /**
   * The evaluation error behind `diagnostic`, kept so the error the renderer
   * reports retains its cause chain. Null when the diagnostic came from the
   * transform, which never has an error object.
   */
  diagnosticError: AiComponentCompileError | null;
  /** Whether any generation has ever committed, so something can render. */
  hasCommit: boolean;
  /**
   * The root that owns the diagnostic of a first generation that never
   * committed, so exactly one renderer shows the compile fallback.
   */
  failedRootKey: string | null;
  /**
   * Whether the failed root has no committed version at all. Its renderer then
   * shows the compile fallback, and there is no "last version that built" for
   * a notice to point at.
   */
  isFailedRootUnbuilt: boolean;
  registerRootSource(kind: PageRuntimeRootKind, sourcePath: string, code: string | null): void;
  releaseRootSource(kind: PageRuntimeRootKind, sourcePath: string): void;
  committedRoot(kind: PageRuntimeRootKind, sourcePath: string): CommittedRootSource | null;
};

export function pageRuntimeRootKey(kind: PageRuntimeRootKind, sourcePath: string): string {
  return `${kind} ${sourcePath}`;
}
